/** Persist Colyseus reconnection across reload, new tab, and (via GET /resume) another browser. */

import { MATCH_RECONNECT_SECONDS } from '@orbomb/protocol';

export interface ResumeSeat {
  token: string;
  supabaseRoomId: string;
  camp: 'ORANGE' | 'TURQUOISE';
  savedAt: number;
}

/**
 * How to enter a lobby row that is already `playing`.
 * - resume: live reconnection token (localStorage or match-server /resume)
 * - join: this tab watched the lobby go from waiting → playing
 * - bounce: no usable token (seat may still be held elsewhere)
 */
export function resolvePlayingEntry(args: {
  sawWaiting: boolean;
  resume: ResumeSeat | null;
  roomId: string;
  camp: 'ORANGE' | 'TURQUOISE';
}): 'resume' | 'join' | 'bounce' {
  const { sawWaiting, resume, roomId, camp } = args;
  if (resume && resume.supabaseRoomId === roomId && resume.camp === camp) return 'resume';
  if (sawWaiting) return 'join';
  return 'bounce';
}

/** localStorage so a closed tab can still resume from a new one. */
const STORAGE_KEY = 'orbomb.colyseus.resume';
/** Seat hold window plus a small clock skew. */
const MAX_AGE_MS = MATCH_RECONNECT_SECONDS * 1000 + 2_000;

function store(): Storage | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage;
  } catch {
    return null;
  }
}

export function saveResumeSeat(seat: Omit<ResumeSeat, 'savedAt'>): void {
  const storage = store();
  if (!storage) return;
  try {
    const payload: ResumeSeat = { ...seat, savedAt: Date.now() };
    storage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* private mode / quota */
  }
}

export function loadResumeSeat(): ResumeSeat | null {
  const storage = store();
  if (!storage) return null;
  try {
    const text = storage.getItem(STORAGE_KEY);
    if (!text) return null;
    const seat = parseResumeFields(JSON.parse(text) as unknown, { requireSavedAt: true });
    if (!seat) {
      clearResumeSeat();
      return null;
    }
    if (Date.now() - seat.savedAt > MAX_AGE_MS) {
      clearResumeSeat();
      return null;
    }
    return seat;
  } catch {
    clearResumeSeat();
    return null;
  }
}

export function clearResumeSeat(): void {
  try {
    store()?.removeItem(STORAGE_KEY);
  } catch {
    /* private mode */
  }
}

export type SeatStatus =
  | { status: 'resumable'; seat: ResumeSeat }
  | { status: 'active' }
  | { status: 'stale' }
  | { status: 'unknown' }
  | { status: 'auth' };

function parseResumeFields(raw: unknown, opts: { requireSavedAt: boolean }): ResumeSeat | null {
  if (!raw || typeof raw !== 'object') return null;
  const body = raw as Partial<ResumeSeat> & { expiresAt?: number };
  if (
    typeof body.token !== 'string' ||
    typeof body.supabaseRoomId !== 'string' ||
    (body.camp !== 'ORANGE' && body.camp !== 'TURQUOISE')
  ) {
    return null;
  }
  if (opts.requireSavedAt && typeof body.savedAt !== 'number') return null;
  if (typeof body.expiresAt === 'number' && body.expiresAt <= Date.now()) return null;
  return {
    token: body.token,
    supabaseRoomId: body.supabaseRoomId,
    camp: body.camp,
    savedAt: typeof body.savedAt === 'number' ? body.savedAt : Date.now(),
  };
}

function rememberParsedSeat(seat: ResumeSeat): ResumeSeat {
  saveResumeSeat({
    token: seat.token,
    supabaseRoomId: seat.supabaseRoomId,
    camp: seat.camp,
  });
  return { ...seat, savedAt: Date.now() };
}

/** Match-server identity reclaim (ADR-026). Requires a dropped seat hold. */
export async function fetchServerResumeSeat(
  colyseusUrl: string,
  accessToken: string,
): Promise<ResumeSeat | null> {
  const base = colyseusUrl.replace(/\/$/, '');
  try {
    const response = await fetch(`${base}/resume`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!response.ok) return null;
    const seat = parseResumeFields(await response.json(), { requireSavedAt: false });
    return seat ? rememberParsedSeat(seat) : null;
  } catch {
    return null;
  }
}

/**
 * resumable — reconnect token available
 * active — Colyseus match still running (another window), or HTTP 403
 * stale — lobby `playing` with no live match (safe to leave_room)
 * unknown — match server unreachable or 5xx (do not treat as active)
 * auth — HTTP 401; refresh sign-in, do not leave_room
 */
export async function fetchSeatStatus(
  colyseusUrl: string,
  accessToken: string,
  supabaseRoomId: string,
): Promise<SeatStatus> {
  const base = colyseusUrl.replace(/\/$/, '');
  const url = `${base}/seat?room=${encodeURIComponent(supabaseRoomId)}`;
  try {
    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (response.status === 401) return { status: 'auth' };
    if (response.status === 403) return { status: 'active' };
    if (!response.ok) {
      // 5xx / unexpected — do not leave_room (may be a blip).
      if (response.status >= 500) return { status: 'unknown' };
      // 404 and other client errors: treat as no live seat.
      return { status: 'stale' };
    }
    const raw = (await response.json()) as { status?: string } & Partial<ResumeSeat> & {
        expiresAt?: number;
      };
    if (raw.status === 'resumable') {
      const seat = parseResumeFields(raw, { requireSavedAt: false });
      if (!seat) return { status: 'stale' };
      return { status: 'resumable', seat: rememberParsedSeat(seat) };
    }
    if (raw.status === 'active') return { status: 'active' };
    return { status: 'stale' };
  } catch {
    return { status: 'unknown' };
  }
}
